import { addDaysToDateString, toDateString } from "../../utils/time";

export interface MonthGridDay {
  date: string;
  dayOfMonth: number;
  isCurrentMonth: boolean;
}

/**
 * Bouwt een maandrooster (weken van 7 dagen, maandag als eerste dag).
 * Werkt uitsluitend met de LOKALE Date-constructor/getters (zie
 * utils/time.ts) zodat kalenderdatums nooit verschuiven afhankelijk van de
 * tijdzone van de machine die de code uitvoert.
 */
export function buildMonthGrid(year: number, month0: number): MonthGridDay[][] {
  const firstOfMonth = new Date(year, month0, 1);
  const jsWeekday = firstOfMonth.getDay(); // 0 = zondag
  const mondayFirstOffset = (jsWeekday + 6) % 7; // 0 = maandag

  const gridStart = new Date(year, month0, 1 - mondayFirstOffset);

  const days: MonthGridDay[] = [];
  const cursor = new Date(gridStart);
  // Maximaal 6 weken (42 dagen) is altijd voldoende om een volledige maand te tonen.
  for (let i = 0; i < 42; i += 1) {
    const dateString = toDateString(cursor);
    days.push({
      date: dateString,
      dayOfMonth: cursor.getDate(),
      isCurrentMonth: cursor.getMonth() === month0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  // Stop na de laatste week die nog dagen van de huidige maand bevat.
  const lastRelevantWeekIndex = Math.ceil(
    days.reduce((max, day, index) => (day.isCurrentMonth ? index : max), 0) / 7,
  );
  const trimmedDays = days.slice(0, (lastRelevantWeekIndex + 1) * 7);

  const weeks: MonthGridDay[][] = [];
  for (let i = 0; i < trimmedDays.length; i += 7) {
    weeks.push(trimmedDays.slice(i, i + 7));
  }
  return weeks;
}

export const WEEKDAY_LABELS_SHORT = ["ma", "di", "wo", "do", "vr", "za", "zo"];
export const WEEKDAY_LABELS_LONG = [
  "maandag",
  "dinsdag",
  "woensdag",
  "donderdag",
  "vrijdag",
  "zaterdag",
  "zondag",
];

export const MONTH_LABELS = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

export function shiftMonth(
  year: number,
  month0: number,
  delta: number,
): { year: number; month0: number } {
  const total = year * 12 + month0 + delta;
  return { year: Math.floor(total / 12), month0: ((total % 12) + 12) % 12 };
}

export function firstDateOfMonth(year: number, month0: number): string {
  return toDateString(new Date(year, month0, 1));
}

export function monthRangeForAvailabilityFetch(
  year: number,
  month0: number,
): { from: string; to: string } {
  // Haal ook enkele dagen uit de aangrenzende maanden op zodat de "outside
  // month"-dagen in het rooster ook correct gemarkeerd kunnen worden.
  const from = addDaysToDateString(firstDateOfMonth(year, month0), -7);
  const nextMonth = shiftMonth(year, month0, 1);
  const to = addDaysToDateString(firstDateOfMonth(nextMonth.year, nextMonth.month0), 7);
  return { from, to };
}
